import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DownloadForm from '../../components/common/DownloadForm'

const FromFundingToAccess: React.FC = () => {
  const navigate = useNavigate()

  const contentRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0)
  const [activeId, setActiveId] = useState<string>('exec-summary')
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  const sections = [
    { id: 'exec-summary' },
    { id: 'new-era-funding' },
    { id: 'expectations' },
    { id: 'reality' },
    { id: 'missing-layer' },
    { id: 'eligibility-bottleneck' },
    { id: 'coordination-gap' },
    { id: 'modern-operating-model' },
    { id: 'framework' },
    { id: 'measuring-roi' },
    { id: 'conclusion' },
    { id: 'about' },
    { id: 'references' },
  ]

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = el.scrollHeight - window.innerHeight + rect.top
      const header = document.getElementById('navigation')
      const headerOffset = header ? header.offsetHeight + 16 : 80
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - headerOffset), 0),
        Math.max(total, 1)
      )
      setProgress((scrolled / Math.max(total, 1)) * 100)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveId(id)
          })
        },
        { rootMargin: '-40% 0px -50% 0px', threshold: 0.01 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [])

  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const header = document.getElementById('navigation')
    const headerOffset = header ? header.offsetHeight + 16 : 80
    window.scrollTo({ top: el.offsetTop - headerOffset, behavior: 'smooth' })
  }

  const MetaItem = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="font-medium text-slate-700">{label}:</span>
      <span>{value}</span>
    </div>
  )

  const imageBase = '/Image/WhitePapers/From Funding to Access'
  const fig1 = `${imageBase}/From Funding to Access Image 2.png`
  const fig2 = `${imageBase}/From Funding to Access Image 3.png`
  const fig3 = `${imageBase}/From Funding to Access Image 4.png`
  const fig4 = `${imageBase}/From Funding to Access Image 5.png`

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="fixed inset-x-0 top-0 z-50 h-1 bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-[width]"
          style={{ width: `${progress}%` }}
        />
      </div>

      <header className="pt-20 pb-10 border-b border-slate-200 bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            White Paper | Public Health Transformation
          </div>

          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
            From Funding to Access
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 text-base md:text-lg">
            Why Public Health Investments Fail Without Operational Infrastructure
          </p>
          <p className="mt-3 max-w-3xl text-slate-700 text-sm md:text-base">
            Bridging the gap between federal dollars and real-world outcomes
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <MetaItem label="Author" value="EceptionistCX Collaborative Healthcare Platform" />
            <MetaItem label="Published" value="April 2026" />
            <MetaItem label="Length" value="14 min read" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => setDownloadFormOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download PDF
            </button>
            <button
              onClick={() => navigator.share?.({ title: 'From Funding to Access', url: window.location.href })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              Share
            </button>
          </div>
        </div>
      </header>

      <main ref={contentRef} className="mx-auto max-w-6xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <article className="lg:col-span-8">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className={`scroll-mt-20 ${i !== 0 ? 'mt-10' : ''}`}>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {s.id === 'exec-summary' && '⭐ Executive Summary'}
                {s.id === 'new-era-funding' && '💰 1. The New Era of Public Health Funding'}
                {s.id === 'expectations' && '🎯 2. The Expectations: What This Funding Is Intended to Achieve'}
                {s.id === 'reality' && '📉 3. The Reality: Outcomes Are Not Keeping Pace'}
                {s.id === 'missing-layer' && '🧩 4. The Missing Layer: Operational Infrastructure'}
                {s.id === 'eligibility-bottleneck' && '🧾 5. Eligibility Verification: A Systemic Bottleneck'}
                {s.id === 'coordination-gap' && '🤝 6. The Coordination Gap Across Public Systems'}
                {s.id === 'modern-operating-model' && '🏗️ 7. What Good Looks Like: A Modern Operating Model'}
                {s.id === 'framework' && '🧭 8. Turning Funding into Outcomes: A Practical Framework'}
                {s.id === 'measuring-roi' && '📊 9. Measuring ROI on Public Health Investments'}
                {s.id === 'conclusion' && '✅ Conclusion'}
                {s.id === 'about' && 'ℹ️ About EceptionistCX'}
                {s.id === 'references' && '📚 References'}
              </h2>

              <div className="mt-4 prose prose-slate max-w-none">
                {s.id === 'exec-summary' && (
                  <>
                    <p>
                      Over the past several years, the United States has seen an unprecedented influx of funding directed
                      toward public health, behavioral health, and healthcare access initiatives. Federal programs —
                      including the American Rescue Plan Act (ARPA), expanded Medicaid funding, and targeted behavioral
                      health investments — have directed billions of dollars to states, counties, and municipalities.
                    </p>
                    <br />
                    <p>
                      Despite this influx of funding, many of the outcomes these investments were intended to improve —
                      such as reduced wait times, increased service utilization, and improved access to care — have not
                      improved at the same pace.
                    </p>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      This disconnect is not primarily a funding problem. It is an operational problem.
                    </blockquote>
                    <br />
                    <p>
                      Across public health and human services systems, organizations have invested heavily in systems of
                      record — EHRs, case management systems, data platforms. However, they have underinvested in the
                      operational infrastructure required to connect those systems and execute care delivery workflows
                      effectively.
                    </p>
                    <br />
                    <p>As a result, funding is often deployed into environments where:</p>
                    <ul className="list-disc pl-6 space-y-2 mt-3">
                      <li>Referrals are fragmented or incomplete</li>
                      <li>Eligibility is unclear or verified too late</li>
                      <li>Scheduling is manual and inefficient</li>
                      <li>Cross-agency coordination is limited or non-existent</li>
                    </ul>
                    <br />
                    <p>
                      This white paper argues that the next phase of public health transformation must focus on workflow
                      orchestration, coordination, and execution infrastructure. Without this layer, even well-funded
                      initiatives will continue to fall short of their intended outcomes.
                    </p>
                  </>
                )}

                {s.id === 'new-era-funding' && (
                  <>
                    <p>
                      Over the past decade — and particularly following the COVID-19 pandemic — public health funding has
                      increased dramatically across multiple dimensions.
                    </p>
                    <br />
                    <p className="font-semibold">Federal Investment Under ARPA</p>
                    <p>The American Rescue Plan Act alone allocated:</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>$3 billion for behavioral health workforce expansion</li>
                      <li>$420 million for Certified Community Behavioral Health Clinics (CCBHCs)</li>
                      <li>Significant funding for state and local public health infrastructure</li>
                    </ul>
                    <br />
                    <p className="font-semibold">Sustained Growth in Medicaid Behavioral Health</p>
                    <p>
                      Alongside ARPA, states and federal agencies have expanded behavioral health programs through multiple
                      channels:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>
                        Medicaid behavioral health spending has steadily increased, now accounting for a significant share
                        of total Medicaid expenditures
                      </li>
                      <li>States have expanded behavioral health programs through Section 1115 waivers</li>
                      <li>School-based mental health funding has grown substantially at the state and local level</li>
                    </ul>
                    <br />
                    <p>
                      Organizations such as the Kaiser Family Foundation and the National Association of State Mental
                      Health Program Directors have documented sustained increases in behavioral health investment across
                      nearly every state.
                    </p>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      Funding has accelerated significantly post-COVID, increasing pressure on systems to deliver
                      measurable, accountable outcomes.
                    </blockquote>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <h5 className="text-base font-semibold mb-2 text-slate-900">
                        Figure 1. Growth in Behavioral Health &amp; Public Health Funding (2015–2023)
                      </h5>
                      <img src={fig1} alt="Growth in Behavioral Health & Public Health Funding (2015–2023)" className="w-full rounded-lg shadow-sm" />
                      <p className="mt-2 text-sm text-slate-600">
                        Sources: KFF, NASMHPD, federal appropriations data
                      </p>
                    </div>
                  </>
                )}

                {s.id === 'expectations' && (
                  <>
                    <p>
                      Public funding initiatives are not simply about allocation — they are tied to clear outcome
                      expectations. Across federal, state, and local programs, the goals are consistent:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>
                        <strong>Reduce wait times for services</strong> — Patients should access care faster, with
                        measurable reductions in time-to-treatment
                      </li>
                      <li>
                        <strong>Increase access to behavioral health care</strong> — More individuals should receive
                        services, particularly in underserved communities
                      </li>
                      <li>
                        <strong>Improve outcomes for underserved populations</strong> — Funding should close documented
                        disparities in care access and quality
                      </li>
                      <li>
                        <strong>Expand school-based and community-based services</strong> — Care should reach people where
                        they are, not just in clinical settings
                      </li>
                      <li>
                        <strong>Improve coordination across systems</strong> — Providers, payers, schools, and community
                        organizations should work in concert
                      </li>
                    </ul>
                    <br />
                    <p className="font-semibold">Accountability Is Built Into the Funding</p>
                    <p>
                      Programs funded under ARPA and Medicaid expansions often include formal reporting requirements tied
                      to performance metrics:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Time-to-treatment metrics</li>
                      <li>Service utilization rates</li>
                      <li>Population-level health outcomes</li>
                    </ul>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      Funding is increasingly tied to performance and accountability — meaning operational gaps are no
                      longer just inefficiencies, they are compliance risks.
                    </blockquote>
                  </>
                )}

                {s.id === 'reality' && (
                  <>
                    <p>
                      Despite significant investment, real-world access challenges persist across behavioral health and
                      public health systems.
                    </p>
                    <br />
                    <p className="font-semibold">The Scale of Unmet Need</p>
                    <p>According to the Health Resources and Services Administration (HRSA):</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Over 160 million Americans live in Mental Health Professional Shortage Areas (HPSAs)</li>
                    </ul>
                    <br />
                    <p className="font-semibold">Operational Friction Is Compounding the Problem</p>
                    <p>
                      Even where providers and funding exist, operational inefficiencies reduce effective system capacity:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>
                        <strong>30–40% no-show rates</strong> are frequently reported in behavioral health settings
                      </li>
                      <li>
                        <strong>Wait times remain long</strong> in many regions, even after significant workforce
                        investment
                      </li>
                      <li>
                        <strong>Referral-to-treatment conversion rates remain inconsistent</strong> across provider types
                        and geographies
                      </li>
                    </ul>
                    <br />
                    <p>
                      Research published in JAMA Network Open and other peer-reviewed journals demonstrates that missed
                      appointments and administrative inefficiencies do not just inconvenience patients — they meaningfully
                      reduce the effective capacity of the entire system.
                    </p>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <h5 className="text-base font-semibold mb-2 text-slate-900">
                        Figure 2. Where Access Breaks Down Across the Care Journey
                      </h5>
                      <img src={fig2} alt="Where Access Breaks Down Across the Care Journey" className="w-full rounded-lg shadow-sm" />
                    </div>
                    <blockquote className="mt-4 border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      Investing in providers and infrastructure without addressing operational execution is like adding
                      lanes to a highway while leaving the on-ramps closed.
                    </blockquote>
                  </>
                )}

                {s.id === 'missing-layer' && (
                  <>
                    <p>
                      Most public health investments have focused on three layers of technology infrastructure:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Systems of record (EHRs, case management platforms, data warehouses)</li>
                      <li>Reporting and analytics platforms</li>
                      <li>Compliance and documentation systems</li>
                    </ul>
                    <br />
                    <p>
                      What they have not adequately addressed is the operational layer — the workflows that connect these
                      systems and enable consistent execution at the point of care.
                    </p>
                    <br />
                    <p className="font-semibold">Where the Gaps Exist</p>
                    <p>
                      The table below summarizes the key operational layers that are most commonly underdeveloped in public
                      health systems:
                    </p>
                    <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-slate-900">Operational Layer</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-900">Current State</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-900">Impact on Access</th>
                          </tr>
                        </thead>
                        <tbody className="[&>tr:not(:last-child)]:border-b">
                          <tr>
                            <td className="px-4 py-3 font-medium">Referral Orchestration</td>
                            <td className="px-4 py-3">
                              Referrals often arrive via fax, phone, or email — no standardized digital pathway
                            </td>
                            <td className="px-4 py-3">
                              Referrals lost or delayed; patients never enter the care pipeline
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">Intake &amp; Triage</td>
                            <td className="px-4 py-3">
                              Intake processes vary by site, clinician, and system; no consistent routing logic
                            </td>
                            <td className="px-4 py-3">
                              Delayed entry to services; inconsistent prioritization of high-risk individuals
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">Scheduling Coordination</td>
                            <td className="px-4 py-3">
                              Scheduling is manual and siloed across teams, often disconnected from clinical capacity
                            </td>
                            <td className="px-4 py-3">
                              Long wait times; preventable no-shows; underutilized resources
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">Eligibility Verification</td>
                            <td className="px-4 py-3">
                              Eligibility is checked late, after scheduling — or not at all until point of service
                            </td>
                            <td className="px-4 py-3">
                              Canceled appointments; redirected referrals; family disengagement
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">Cross-Agency Communication</td>
                            <td className="px-4 py-3">
                              No shared visibility across providers, schools, payers, and community organizations
                            </td>
                            <td className="px-4 py-3">
                              Duplicated effort; inconsistent care; gaps in accountability
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <br />
                    <p>
                      The result is a system where data exists, but workflows are fragmented; systems are integrated, but
                      processes are not; and funding is available, but execution is inconsistent.
                    </p>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      A system of record tells you what happened. A system of execution ensures that the right things
                      happen, in the right order, by the right people, at the right time.
                    </blockquote>
                  </>
                )}

                {s.id === 'eligibility-bottleneck' && (
                  <>
                    <p>
                      Eligibility is one of the most critical — and most overlooked — points of failure in access to care.
                      In programs such as Medicaid, the challenge is structural:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Coverage status can change frequently, a phenomenon known as “churn”</li>
                      <li>Patients may move in and out of eligibility based on income, employment, or family status</li>
                      <li>Network participation varies by provider, creating additional verification complexity</li>
                    </ul>
                    <br />
                    <p className="font-semibold">The Scale of the Problem</p>
                    <p>
                      The Kaiser Family Foundation has documented that Medicaid churn affects a significant percentage of
                      enrollees annually, particularly among children and low-income populations. The CAQH Index estimates
                      that the U.S. healthcare system spends tens of billions annually on administrative transactions —
                      with eligibility verification representing one of the largest categories.
                    </p>
                    <br />
                    <p className="font-semibold">The Cost of Late Verification</p>
                    <p>
                      When eligibility is not verified until the point of service — or not at all before scheduling — the
                      downstream effects are significant:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>
                        <strong>Appointments are canceled or rescheduled</strong> creating gaps in care and provider
                        underutilization
                      </li>
                      <li>
                        <strong>Referrals must be redirected</strong> consuming staff time and delaying patient access
                      </li>
                      <li>
                        <strong>Families disengage</strong> particularly those who face barriers to transportation,
                        childcare, or time off work
                      </li>
                    </ul>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <h5 className="text-base font-semibold mb-2 text-slate-900">
                        Figure 3. Impact of Eligibility Timing on Successful Access Outcomes
                      </h5>
                      <img src={fig3} alt="Impact of Eligibility Timing on Successful Access Outcomes" className="w-full rounded-lg shadow-sm" />
                    </div>
                    <br />
                    <p className="font-semibold">The Solution: Embed Eligibility Early</p>
                    <p>
                      Embedding eligibility checks at the point of referral — rather than at the point of scheduling or
                      service — produces measurable improvements:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Reduces administrative rework and reschedule burden</li>
                      <li>Improves referral-to-visit conversion rates</li>
                      <li>Prevents avoidable delays and disengagement</li>
                      <li>Enables earlier redirection to appropriate funding sources when needed</li>
                    </ul>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      Early eligibility verification is not just an administrative improvement — it is a direct driver of
                      access and equity.
                    </blockquote>
                  </>
                )}

                {s.id === 'coordination-gap' && (
                  <>
                    <p>
                      Public health care is not delivered by a single organization — it is delivered by networks. A
                      behavioral health patient may interact with a primary care provider, a specialty clinic, a school
                      counselor, a community-based organization (CBO), and a payer — sometimes all within the same episode
                      of care.
                    </p>
                    <br />
                    <p className="font-semibold">The Challenge: Independent Systems, No Shared Operational Layer</p>
                    <p>These organizations typically:</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Operate independently, with distinct governance structures and priorities</li>
                      <li>Use different platforms and electronic systems with limited interoperability</li>
                      <li>Follow different workflows, intake processes, and documentation standards</li>
                    </ul>
                    <br />
                    <p>There is no shared operational layer connecting them. The result is predictable:</p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>
                        <strong>Duplication of effort</strong> — patients are assessed multiple times; information is
                        re-entered manually across systems
                      </li>
                      <li>
                        <strong>Inconsistent data</strong> — different organizations hold different versions of a
                        patient’s status, history, or needs
                      </li>
                      <li>
                        <strong>Delays in care delivery</strong> — handoffs between organizations create gaps, and no
                        single party has visibility into what the other is doing
                      </li>
                      <li>
                        <strong>Lack of accountability</strong> — when things fall through the cracks, it is difficult to
                        determine where or why
                      </li>
                    </ul>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <h5 className="text-base font-semibold mb-2 text-slate-900">
                        Figure 4. The Coordination Gap: Fragmented vs. Connected Systems
                      </h5>
                      <img src={fig4} alt="The Coordination Gap: Fragmented vs. Connected Systems" className="w-full rounded-lg shadow-sm" />
                    </div>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      Coordination is not a problem that technology alone solves. It requires shared workflows, shared
                      visibility, and shared accountability — all of which require operational infrastructure.
                    </blockquote>
                  </>
                )}

                {s.id === 'modern-operating-model' && (
                  <>
                    <p>
                      A modern public health operating model is not defined by which systems are in place — it is defined
                      by how effectively those systems execute care delivery workflows. The five capabilities below
                      represent the operational foundation that high-performing systems share:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>
                        <strong>Guided referral pathways</strong> — Referrals enter a structured, digital workflow with
                        defined triage logic, ownership, and escalation paths
                      </li>
                      <li>
                        <strong>Real-time eligibility verification</strong> — Coverage is confirmed at the point of
                        referral, not at the point of service
                      </li>
                      <li>
                        <strong>Coordinated scheduling workflows</strong> — Scheduling is connected to clinical capacity,
                        provider availability, and patient needs
                      </li>
                      <li>
                        <strong>Cross-organization visibility</strong> — Providers, schools, payers, and CBOs share a
                        common view of a patient’s status and next steps
                      </li>
                      <li>
                        <strong>Longitudinal tracking across services</strong> — Each individual’s journey across
                        multiple organizations and episodes of care is captured in a coherent, auditable record
                      </li>
                    </ul>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      This is not a new system of record. It is a system of execution — the layer that determines whether
                      funding becomes outcomes.
                    </blockquote>
                  </>
                )}

                {s.id === 'framework' && (
                  <>
                    <p>
                      Organizations can translate public health funding into measurable outcomes through a phased
                      operational approach. Each phase builds on the previous, creating compounding improvements in
                      access, efficiency, and accountability.
                    </p>
                    <br />
                    <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200 bg-white">
                      <table className="min-w-full text-sm">
                        <tbody className="[&>tr:not(:last-child)]:border-b">
                          <tr>
                            <td className="px-4 py-3 font-semibold text-slate-900">
                              Phase 1 — Standardize Intake &amp; Referral
                            </td>
                            <td className="px-4 py-3">
                              <ul className="list-disc pl-5 space-y-1">
                                <li>Establish a consistent digital referral pathway</li>
                                <li>Create standardized intake forms and triage criteria</li>
                                <li>Assign clear ownership for each intake step</li>
                                <li>Ensure all referrals enter a tracked, auditable queue</li>
                              </ul>
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-semibold text-slate-900">
                              Phase 2 — Digitize Scheduling &amp; Engagement
                            </td>
                            <td className="px-4 py-3">
                              <ul className="list-disc pl-5 space-y-1">
                                <li>Implement coordinated scheduling connected to capacity</li>
                                <li>Automate appointment reminders and follow-up outreach</li>
                                <li>Enable self-scheduling or supported scheduling for families</li>
                                <li>Capture no-show and cancellation data for improvement</li>
                              </ul>
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-semibold text-slate-900">
                              Phase 3 — Embed Eligibility Workflows Early
                            </td>
                            <td className="px-4 py-3">
                              <ul className="list-disc pl-5 space-y-1">
                                <li>Verify insurance and program eligibility at the point of referral</li>
                                <li>Integrate payer data to flag coverage changes in real time</li>
                                <li>Automate eligibility re-verification for longitudinal cases</li>
                                <li>Route patients to appropriate funding sources based on verified status</li>
                              </ul>
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-semibold text-slate-900">
                              Phase 4 — Measure, Learn &amp; Optimize
                            </td>
                            <td className="px-4 py-3">
                              <ul className="list-disc pl-5 space-y-1">
                                <li>Track key access metrics</li>
                                <li>Establish feedback loops between operations and clinical leadership</li>
                                <li>Identify and address bottlenecks on a rolling basis</li>
                                <li>Tie performance data to funding accountability requirements</li>
                              </ul>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <br />
                    <p>
                      This framework is not prescriptive in its sequencing — organizations may need to begin at different
                      phases based on their current state. However, experience across health systems and public sector
                      organizations suggests that attempting Phase 4 without Phases 1–3 in place produces data that
                      reveals problems without providing the operational foundation needed to address them.
                    </p>
                  </>
                )}

                {s.id === 'measuring-roi' && (
                  <>
                    <p>
                      As public health funding becomes increasingly tied to performance accountability, organizations need
                      a clear set of operational metrics that connect day-to-day execution to program-level outcomes. The
                      following metrics provide a practical measurement framework:
                    </p>
                    <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-slate-900">Metric</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-900">What It Measures</th>
                          </tr>
                        </thead>
                        <tbody className="[&>tr:not(:last-child)]:border-b">
                          <tr>
                            <td className="px-4 py-3 font-medium">Time to First Appointment</td>
                            <td className="px-4 py-3">Measures how quickly patients move from referral to scheduled visit</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">Referral-to-Visit Conversion Rate</td>
                            <td className="px-4 py-3">Tracks the percentage of referrals that successfully complete a first appointment</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">No-Show / Cancellation Rate</td>
                            <td className="px-4 py-3">Indicates scheduling friction and engagement gaps in the care pathway</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">Eligibility Exception Rate</td>
                            <td className="px-4 py-3">Captures how often late eligibility checks disrupt appointments or require rework</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">Cross-Agency Coordination Frequency</td>
                            <td className="px-4 py-3">Measures active collaboration between providers, schools, payers, and CBOs</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-3 font-medium">Capacity Utilization</td>
                            <td className="px-4 py-3">Reflects how effectively available appointments, staff, and resources are being used</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <br />
                    <p>
                      These metrics are meaningful not just for internal performance management — they are increasingly
                      required for federal and state reporting. Organizations that build the operational infrastructure to
                      capture these metrics consistently will be better positioned to demonstrate value, justify continued
                      funding, and identify improvement opportunities.
                    </p>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      What gets measured gets managed — but only if the operational infrastructure exists to generate
                      reliable data in the first place.
                    </blockquote>
                  </>
                )}

                {s.id === 'conclusion' && (
                  <>
                    <p>
                      The current moment represents a historic opportunity for public health transformation. Funding is at
                      historic levels. Political will is present. The need is clear and well-documented.
                    </p>
                    <br />
                    <p>
                      But funding alone is not sufficient. The organizations that will succeed in translating these
                      investments into measurable outcomes are those that build the operational infrastructure to execute
                      consistently — across referrals, eligibility, scheduling, and cross-agency coordination.
                    </p>
                    <br />
                    <blockquote className="border-l-4 border-slate-300 pl-4 italic text-slate-700">
                      <strong>Funding is not the limiting factor. Execution is.</strong>
                    </blockquote>
                    <br />
                    <p>
                      The next phase of public health transformation will not be defined by how much is spent — but by how
                      effectively systems coordinate to deliver care. Organizations that invest in operational
                      infrastructure alongside clinical and data infrastructure will be the ones that demonstrate results,
                      sustain funding, and improve lives.
                    </p>
                    <button
                      onClick={() => setDownloadFormOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download Full Paper
                    </button>
                  </>
                )}

                {s.id === 'about' && (
                  <>
                    <p>
                      EceptionistCX is a collaborative care coordination platform designed to support complex referral,
                      scheduling, and engagement workflows across healthcare, public sector, and community-based
                      environments.
                    </p>
                    <br />
                    <p>
                      The platform helps organizations improve access, reduce administrative burden, and create clearer
                      pathways to care — particularly in systems where coordination across multiple stakeholders is
                      essential. EceptionistCX has been delivering secure, cloud-based workflow solutions for more than
                      two decades, supporting healthcare organizations around the world.
                    </p>
                    <br />
                    <p>
                      EceptionistCX platform capabilities include modular support for referrals, scheduling, telehealth,
                      eConsults, case management and complex pathway management. The platform is designed to complement
                      existing systems of record — not replace them — by providing the operational infrastructure required
                      to scale programs safely, efficiently, and compliantly.
                    </p>
                  </>
                )}

                {s.id === 'references' && (
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>American Rescue Plan Act (2021). Public Law 117-2. U.S. Congress.</li>
                    <li>Kaiser Family Foundation (KFF). Medicaid and Behavioral Health Coverage Data. kff.org.</li>
                    <li>Health Resources and Services Administration (HRSA). Health Professional Shortage Area (HPSA) Data. hrsa.gov.</li>
                    <li>CAQH Index Reports. Closing the Gap: The Industry’s Call to Action. caqh.org.</li>
                    <li>National Association of State Mental Health Program Directors (NASMHPD). Behavioral health investment and workforce data. nasmhpd.org.</li>
                    <li>JAMA Network Open. Behavioral health utilization, no-show rates, and access studies. jamanetwork.com.</li>
                    <li>Centers for Medicare and Medicaid Services (CMS). Medicaid Section 1115 Waiver Data. medicaid.gov.</li>
                  </ol>
                )}
              </div>
            </section>
          ))}
        </article>

        <aside className="lg:col-span-4 lg:pl-2">
          <div className="lg:sticky lg:top-32 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Summary</div>
              <p className="mt-2 text-sm text-slate-700">
                Public health funding is at historic levels, but outcomes won’t follow unless systems build the operational
                infrastructure to execute workflows across referrals, eligibility, scheduling, and coordination.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>Execution infrastructure vs. systems of record</li>
                <li>Eligibility verification as a bottleneck</li>
                <li>Cross-agency coordination and shared visibility</li>
                <li>Operational metrics tied to accountability</li>
              </ul>
              <button
                onClick={() => setDownloadFormOpen(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download PDF
              </button>
            </div>

            <nav className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Table of Contents</div>
              <ul className="mt-3 space-y-2">
                {sections.map((sec) => (
                  <li key={sec.id}>
                    <button
                      onClick={() => scrollTo(sec.id)}
                      className={
                        'text-left text-sm w-full rounded-lg px-2 py-1 hover:bg-slate-50 ' +
                        (activeId === sec.id ? 'font-semibold text-slate-900' : 'text-slate-700')
                      }
                    >
                      {sec.id === 'exec-summary' && '⭐ Executive Summary'}
                      {sec.id === 'new-era-funding' && '💰 1. The New Era of Public Health Funding'}
                      {sec.id === 'expectations' && '🎯 2. The Expectations'}
                      {sec.id === 'reality' && '📉 3. The Reality'}
                      {sec.id === 'missing-layer' && '🧩 4. The Missing Layer'}
                      {sec.id === 'eligibility-bottleneck' && '🧾 5. Eligibility Verification'}
                      {sec.id === 'coordination-gap' && '🤝 6. The Coordination Gap'}
                      {sec.id === 'modern-operating-model' && '🏗️ 7. What Good Looks Like'}
                      {sec.id === 'framework' && '🧭 8. Practical Framework'}
                      {sec.id === 'measuring-roi' && '📊 9. Measuring ROI'}
                      {sec.id === 'conclusion' && '✅ Conclusion'}
                      {sec.id === 'about' && 'ℹ️ About EceptionistCX'}
                      {sec.id === 'references' && '📚 References'}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            <div id="cite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Citation</div>
              <p className="mt-2 text-xs text-slate-700">
                EceptionistCX Collaborative Healthcare Platform. &quot;From Funding to Access: Why Public Health Investments Fail Without Operational Infrastructure.&quot; April 2026.
              </p>
              <button
                onClick={() => {
                  const text =
                    'EceptionistCX Collaborative Healthcare Platform. \"From Funding to Access: Why Public Health Investments Fail Without Operational Infrastructure.\" April 2026.'
                  navigator.clipboard?.writeText(text)
                }}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                Copy Citation
              </button>
            </div>
          </div>
        </aside>
      </main>

      <section className="border-t border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto max-w-6xl px-4">
          <h3 className="text-xl md:text-2xl font-semibold text-slate-900">Related White Papers</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'From Eligibility to Engagement', description: 'Fixing the broken front door to care by embedding eligibility earlier.', route: '/whitepapers/from-eligibility-to-engagement' },
              { title: 'The Future of Referral Management in Healthcare', description: 'From fax to AI-powered workflows that improve access and reduce waste.', route: '/whitepapers/referral-cx' },
              { title: 'Bridging the Youth Mental Health Access Gap', description: 'Why coordination and eligibility automation unlock timely care.', route: '/whitepapers/bridging-youth-mental-health-access-gap' },
            ].map((card, index: number) => (
              <button
                key={index}
                onClick={() => navigate(card.route)}
                className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow text-left w-full"
              >
                <div className="flex items-start justify-between gap-4">
                  <h4 className="text-base font-semibold text-slate-900 group-hover:underline">
                    {card.title}
                  </h4>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white">→</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{card.description}</p>
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button
              onClick={() => navigate('/whitepapers')}
              className="text-sm font-medium text-slate-700 hover:underline"
            >
              ← Back to White Papers
            </button>
          </div>
        </div>
      </section>

      <div className="h-8" />

      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="From Funding to Access: Why Public Health Investments Fail Without Operational Infrastructure"
        pdfPath="/PDF/WhitePapers/From_Funding_to_Access_WhitePaper.pdf"
      />
    </div>
  )
}

export default FromFundingToAccess

